<template>
  <div class="min-h-full py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6 text-slate-900 dark:text-[#F1F5F9] transition-colors">
    <div class="max-w-4xl mx-auto space-y-6">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2"
      >
        <div class="flex-1">
          <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/40 mb-2">
            School Configuration
          </span>
          <h1
            class="text-xl sm:text-2xl lg:text-3xl font-black text-slate-900 dark:text-white tracking-tight"
          >
            Edit School Details
          </h1>
          <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Update institutional metadata, physical address, and communication channels
          </p>
        </div>

        <div class="flex items-center gap-2.5">
          <button
            @click="$router.go(-1)"
            class="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E49] text-slate-700 dark:text-slate-300 font-semibold text-xs sm:text-sm rounded-xl border border-slate-200 dark:border-[#263449] transition-colors flex items-center gap-2"
          >
            <ArrowLeft class="w-4 h-4" />
            <span>Cancel</span>
          </button>

          <button
            @click="saveChanges"
            :disabled="isLoading"
            class="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-sm shadow-indigo-600/20 transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <Save class="w-4 h-4" />
            <span>{{ isLoading ? "Saving..." : "Save Changes" }}</span>
          </button>
        </div>
      </div>

      <!-- Edit Form -->
      <form
        @submit.prevent="saveChanges"
        class="space-y-6"
      >
        <!-- Basic Information -->
        <div
          class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-xs border border-slate-200/90 dark:border-[#263449] p-5 sm:p-7 space-y-6"
        >
          <div class="flex items-center gap-3 pb-4 border-b border-slate-100 dark:border-[#263449]">
            <div
              class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-900/40"
            >
              <Info class="w-5 h-5" />
            </div>
            <div>
              <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                Basic Information
              </h3>
              <p class="text-xs text-slate-400 dark:text-slate-500">Official name, category, and foundation year</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                School Name <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="formData.schoolName"
                type="text"
                required
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="Enter school name"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                School Type <span class="text-rose-500">*</span>
              </label>
              <select
                v-model="formData.schoolType"
                required
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
              >
                <option value="">Select school type</option>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="high">High</option>
                <option value="college">College</option>
                <option value="university">University</option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Establishment Year
              </label>
              <input
                v-model="formData.establishmentYear"
                type="number"
                min="1800"
                :max="new Date().getFullYear()"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="e.g., 1995"
              />
            </div>

            <div class="sm:col-span-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Description
              </label>
              <textarea
                v-model="formData.description"
                rows="3"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors resize-none text-sm"
                placeholder="Brief description about the school..."
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Address Information -->
        <div
          class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-xs border border-slate-200/90 dark:border-[#263449] p-5 sm:p-7 space-y-6"
        >
          <div class="flex items-center gap-3 pb-4 border-b border-slate-100 dark:border-[#263449]">
            <div
              class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-100 dark:border-emerald-900/40"
            >
              <MapPin class="w-5 h-5" />
            </div>
            <div>
              <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                Campus Location
              </h3>
              <p class="text-xs text-slate-400 dark:text-slate-500">Physical address, municipality, and jurisdiction</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <div class="sm:col-span-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Street Address
              </label>
              <textarea
                v-model="formData.address"
                rows="2"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors resize-none text-sm"
                placeholder="Enter complete street address"
              ></textarea>
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Village / Area
              </label>
              <input
                v-model="formData.village"
                type="text"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="Village or area name"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Post Office
              </label>
              <input
                v-model="formData.postOffice"
                type="text"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="Post office name"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                City
              </label>
              <input
                v-model="formData.city"
                type="text"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="City name"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                State
              </label>
              <input
                v-model="formData.state"
                type="text"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="State name"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                PIN Code <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="formData.pincode"
                type="text"
                required
                pattern="[0-9]{6}"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="6-digit PIN code"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Country <span class="text-rose-500">*</span>
              </label>
              <select
                v-model="formData.country"
                required
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
              >
                <option value="">Select country</option>
                <option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Contact Information -->
        <div
          class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-xs border border-slate-200/90 dark:border-[#263449] p-5 sm:p-7 space-y-6"
        >
          <div class="flex items-center gap-3 pb-4 border-b border-slate-100 dark:border-[#263449]">
            <div
              class="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0 border border-purple-100 dark:border-purple-900/40"
            >
              <Phone class="w-5 h-5" />
            </div>
            <div>
              <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                Contact Channels
              </h3>
              <p class="text-xs text-slate-400 dark:text-slate-500">Phone numbers, institutional email, and website</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Primary Phone <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="formData.primaryPhone"
                type="tel"
                required
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="Primary contact number"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Secondary Phone
              </label>
              <input
                v-model="formData.secondaryPhone"
                type="tel"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="Secondary contact number"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Email Address
              </label>
              <input
                v-model="formData.email"
                type="email"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="school@example.com"
              />
            </div>

            <div>
              <label class="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                Website URL
              </label>
              <input
                v-model="formData.website"
                type="url"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors text-sm"
                placeholder="https://www.schoolwebsite.com"
              />
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div
          class="flex items-center justify-end gap-3 pt-2"
        >
          <button
            type="button"
            @click="$router.go(-1)"
            class="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E49] text-slate-700 dark:text-slate-300 font-semibold rounded-xl border border-slate-200 dark:border-[#263449] transition-colors text-sm"
          >
            Cancel
          </button>

          <button
            type="submit"
            :disabled="isLoading"
            class="px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 shadow-sm shadow-indigo-600/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          >
            <Save class="w-4 h-4" />
            <span>{{ isLoading ? "Saving Changes..." : "Save Changes" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted, computed } from "vue";
  import { useRouter } from "vue-router";
  import { useStore } from "vuex";
  import { ArrowLeft, Save, Info, MapPin, Phone } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  const router = useRouter();
  const store = useStore();
  const { showToast } = useGlobalToast();

  // Loading state
  const isLoading = ref(false);

  // Countries list
  const countries = ref([
    { code: "US", name: "United States" },
    { code: "IN", name: "India" },
    { code: "UK", name: "United Kingdom" },
    { code: "CA", name: "Canada" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "JP", name: "Japan" },
    { code: "CN", name: "China" },
    { code: "BR", name: "Brazil" },
  ]);

  // Form data
  const formData = reactive({
    schoolName: "",
    schoolType: "",
    establishmentYear: "",
    description: "",
    address: "",
    village: "",
    postOffice: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    primaryPhone: "",
    secondaryPhone: "",
    email: "",
    website: "",
  });

  // Get current school data from store
  const schoolData = computed(() => store.state.schools[0] || null);

  // Initialize form with existing data
  onMounted(async () => {
    // Fetch current school data if not available
    if (!schoolData.value) {
      await store.dispatch("fetchMySchool");
    }

    // Populate form with existing data
    if (schoolData.value) {
      Object.keys(formData).forEach((key) => {
        if (schoolData.value[key] !== undefined) {
          formData[key] = schoolData.value[key] || "";
        }
      });
    }
  });

  // Save changes
  const saveChanges = async () => {
    try {
      isLoading.value = true;

      const updateData = { ...formData };

      if (updateData.establishmentYear) {
        updateData.establishmentYear = parseInt(updateData.establishmentYear);
      }

      await store.dispatch("editSchool", updateData);

      // ✅ Show success toast
      showToast("success", "School details updated successfully!");

      router.go(-1);
    } catch (error) {
      console.error("Error updating school details:", error);

      // ❌ Error toast
      showToast("error", "Failed to update school details.");
    } finally {
      isLoading.value = false;
    }
  };
</script>
